import { Award, Flag } from 'lucide-react';
import { OMS_COLORS, tint } from '../omsDashTheme';
import type { PerfNode } from '../utils/performanceModel';
import { DeltaChip, ScoreDial } from './HealthScore';
import { formatNumber } from '../utils/formatNumber';

interface RailProps {
  title: string;
  tone: 'positive' | 'negative';
  nodes: PerfNode[];
  deltaPhrase: string;
  onSelect: (node: PerfNode) => void;
}

function Rail({ title, tone, nodes, deltaPhrase, onSelect }: RailProps) {
  const accent = tone === 'positive' ? '#1F5A47' : '#9F1239';
  const Icon = tone === 'positive' ? Award : Flag;
  return (
    <div
      className="rounded-xl border border-[rgba(11,59,46,0.18)] p-3"
      style={{ backgroundColor: tone === 'positive' ? tint('#339274', 0.06) : tint('#9F1239', 0.05) }}
    >
      <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: accent }}>
        <Icon className="h-3 w-3" /> {title}
      </p>
      {nodes.length === 0 ? (
        <p className="mt-2 text-[11px] font-medium" style={{ color: OMS_COLORS.caption }}>
          {tone === 'positive' ? 'No scored entities yet in this window.' : 'Nothing flagged in this window.'}
        </p>
      ) : (
        <ul className="mt-1.5 space-y-0.5">
          {nodes.map((node, i) => (
            <li key={node.id}>
              <button
                type="button"
                onClick={() => onSelect(node)}
                className="group flex w-full items-center gap-2 rounded-lg px-1.5 py-1.5 text-left transition hover:bg-[rgba(11,59,46,0.05)]"
              >
                <span className="w-4 shrink-0 text-[10px] font-extrabold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] font-bold leading-tight" style={{ color: OMS_COLORS.tealDark }}>
                    {node.name}
                  </span>
                  <DeltaChip delta={node.delta} phrase={`pts ${deltaPhrase}`} />
                </span>
                {/* Ring gauge, never a bare numeral — score vs count stays unmistakable. */}
                <span
                  className="shrink-0"
                  title={
                    node.score
                      ? `Health score ${node.score.score}/100 · ${formatNumber(node.completed)} of ${formatNumber(node.scheduled)} tasks completed`
                      : 'No scheduled work'
                  }
                >
                  <ScoreDial score={node.score} size={40} label={`Health score for ${node.name}`} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface Props {
  rows: PerfNode[];
  deltaPhrase: string;
  onSelect: (node: PerfNode) => void;
}

/**
 * Top performers / Needs attention — recognition and triage carry equal
 * prominence at every Explorer level. Both lists come from the same scored
 * ranking, so they can never contradict the table beside them.
 */
export default function PerfRails({ rows, deltaPhrase, onSelect }: Props) {
  const scored = rows.filter((r) => r.score !== null);
  const top = scored.slice(0, 3);
  const topIds = new Set(top.map((n) => n.id));
  const bottom = scored
    .filter((n) => !topIds.has(n.id) && n.score!.band !== 'excellent' && n.score!.band !== 'good')
    .slice(-3)
    .reverse();
  return (
    <div className="flex flex-col gap-3">
      <Rail title="Top performers" tone="positive" nodes={top} deltaPhrase={deltaPhrase} onSelect={onSelect} />
      <Rail title="Needs attention" tone="negative" nodes={bottom} deltaPhrase={deltaPhrase} onSelect={onSelect} />
    </div>
  );
}
